import { useCallback, useEffect, useMemo, useState } from "react";
import { MessageCircle } from "lucide-react";
import { http, errMsg, AVAIL_LABEL } from "@/lib/api";
import { Packages } from "@/components/site/Packages";
import { SiteCtx } from "@/context/SiteContext";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { ServiceExplorer } from "@/components/site/ServiceExplorer";
import { HomecareInfo } from "@/components/site/HomecareInfo";
import { PriceList } from "@/components/site/PriceList";
import { Gallery } from "@/components/site/Gallery";
import { Testimonials } from "@/components/site/Testimonials";
import { Faq } from "@/components/site/Faq";
import { SocialSection } from "@/components/site/SocialSection";
import { ContactSection } from "@/components/site/ContactSection";
import { Footer } from "@/components/site/Footer";
import { InquiryDialog } from "@/components/site/InquiryDialog";
import { Lightbox } from "@/components/site/Lightbox";

const StatusStrip = ({ s }) =>
  ["full", "temp_closed", "closed"].includes(s.service_status) || s.service_status_note ? (
    <div className="bg-nanina-butter/60 text-nanina-green text-xs text-center px-4 py-2" data-testid="status-strip">
      <b>{AVAIL_LABEL[s.service_status] || "Info layanan"}</b>{s.service_status_note ? ` · ${s.service_status_note}` : ""}
    </div>
  ) : null;

export default function PublicSite({ preview = false }) {
  const [site, setSite] = useState(null);
  const [error, setError] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [inquiry, setInquiry] = useState({ open: false });
  const [lightbox, setLightbox] = useState({ open: false });

  const load = useCallback(() => {
    setError("");
    http.get(preview ? "/admin/preview/site" : "/public/site").then((r) => {
      setSite(r.data);
      setActiveId(r.data.categories[0]?.id || null);
    }).catch((e) => setError(errMsg(e)));
  }, [preview]);
  useEffect(() => { load(); }, [load]);

  const ctx = useMemo(() => ({
    site,
    activeId,
    selectService: setActiveId,
    openInquiry: (service = "", homecare) => setInquiry({ open: true, service, homecare: homecare || (service.startsWith("Homecare") ? "ya" : "tidak") }),
    openLightbox: (photos, index) => setLightbox({ open: true, photos, index }),
  }), [site, activeId]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="site-error">
        <div>
          <img src="/logo-nanina.png" alt="Nanina Care" className="h-16 w-16 mx-auto" />
          <p className="mt-3 text-nanina-green font-semibold">Gagal memuat website.</p>
          <p className="text-xs text-nanina-muted">{error}</p>
          <button onClick={load} className="mt-4 rounded-full bg-nanina-green text-white text-sm px-5 py-2.5" data-testid="site-retry-button">Coba lagi</button>
        </div>
      </div>
    );
  }
  if (!site) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="site-loading">
        <img src="/logo-nanina.png" alt="Memuat Nanina Care" className="h-16 w-16 animate-pulse" />
      </div>
    );
  }

  return (
    <SiteCtx.Provider value={ctx}>
      {preview && (
        <div className="bg-nanina-butter text-nanina-green text-xs text-center px-4 py-2 font-semibold" data-testid="preview-banner">
          Mode Pratinjau: menampilkan draft. Pengunjung belum melihat perubahan ini.{" "}
          <a href="/admin" className="underline">Kembali ke dashboard</a>
        </div>
      )}
      <StatusStrip s={site.settings} />
      <Header />
      <main>
        <Hero />
        <ServiceExplorer />
        <Packages />
        <HomecareInfo />
        <PriceList />
        <Gallery />
        <Testimonials />
        <Faq />
        <SocialSection />
        <ContactSection />
      </main>
      <Footer />
      <button onClick={() => ctx.openInquiry()} className="fixed bottom-4 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-nanina-green text-white text-xs sm:text-sm font-semibold px-4 py-3 shadow-[0_15px_35px_rgba(49,95,82,0.38)] hover:-translate-y-0.5 transition-transform" data-testid="floating-whatsapp-button">
        <MessageCircle className="h-4 w-4" /> WhatsApp
      </button>
      <InquiryDialog state={inquiry} onClose={() => setInquiry({ open: false })} site={site} />
      <Lightbox state={lightbox} onChange={setLightbox} />
    </SiteCtx.Provider>
  );
}
